<template>
  <div
    role="progressbar"
    aria-valuemin="0"
    aria-valuemax="100"
    class="v-progress-circular"
    :class="classes"
    :style="styles"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      :viewBox="`${viewBoxSize} ${viewBoxSize} ${2 * viewBoxSize} ${2 * viewBoxSize}`"
      style="transform: rotate(0deg)"
    >
      <circle
        class="v-progress-circular__overlay"
        v-bind="{
          fill: 'transparent',
          cx: 2 * viewBoxSize,
          cy: 2 * viewBoxSize,
          r: radius,
          'stroke-width': strokeWidth,
          'stroke-dasharray': strokeDashArray,
          'stroke-dashoffset': strokeDashOffset,
        }"
      ></circle>
    </svg>
    <div class="v-progress-circular__info"><slot /></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  size?: number;
  width?: number;
  indeterminate?: boolean;
  value?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 50,
  width: 4,
  indeterminate: true,
  value: 0,
});

const isVisible = true;
const radius = 20;

const calculatedSize = computed(() => props.size);
const viewBoxSize = computed(() => radius / (1 - props.width / props.size));

const normalizedValue = computed(() => {
  if (props.value < 0) return 0;
  if (props.value > 100) return 100;
  return props.value;
});
const circumference = computed(() => 2 * Math.PI * radius);
const strokeDashArray = computed(() => Math.round(circumference.value * 1000) / 1000);
const strokeDashOffset = computed(() => ((100 - normalizedValue.value) / 100) * circumference.value + 'px');
const strokeWidth = computed(() => (props.width / props.size) * viewBoxSize.value * 2);

const styles = computed(() => ({
  width: calculatedSize.value + 'px',
  height: calculatedSize.value + 'px',
}));

const classes = computed(() => ({
  'v-progress-circular--visible': isVisible,
  'v-progress-circular--indeterminate': props.indeterminate,
}));
</script>

<style lang="scss" scoped>
$progress-circular-rotate-animation: progress-circular-rotate 1.4s linear infinite !default;
$progress-circular-rotate-dash: progress-circular-dash 1.4s ease-in-out infinite !default;
$process-circular-intermediate-svg-transition: all 0.2s ease-in-out !default;
$progress-circular-underlay-stroke: rgba(#9e9e9e, 0.4) !default;
$progress-circular-overlay-transition: all 0.6s ease-in-out !default;

.v-progress-circular {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  justify-content: center;
  align-items: center;
  color: $color-grey;

  > svg {
    width: 100%;
    height: 100%;
    margin: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 0;
  }

  &__info {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  &__underlay {
    stroke: $progress-circular-underlay-stroke;
    z-index: 1;
  }

  &__overlay {
    stroke: currentColor;
    z-index: 2;
    transition: $progress-circular-overlay-transition;
  }

  &--indeterminate {
    > svg {
      animation: $progress-circular-rotate-animation;
      transform-origin: center center;
      transition: $process-circular-intermediate-svg-transition;
    }

    .v-progress-circular__overlay {
      animation: $progress-circular-rotate-dash;
      stroke-linecap: round;
      stroke-dasharray: 80, 200;
      stroke-dashoffset: 0;
    }
  }

  &--indeterminate:not(&--visible) {
    > svg,
    .v-progress-circular__overlay {
      animation-play-state: paused !important;
    }
  }
}

@keyframes progress-circular-dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }

  50% {
    stroke-dasharray: 100, 200;
    stroke-dashoffset: -15px;
  }

  100% {
    stroke-dasharray: 100, 200;
    stroke-dashoffset: -124px;
  }
}

@keyframes progress-circular-rotate {
  100% {
    transform: rotate(360deg);
  }
}
</style>
